import type { Item } from '@/components/fields/KSelectField.vue';
import type {
  BookingStop,
  JobBookingCustomer,
} from '@/models/advancedJobBooking';

/** A single produce/package line on a PAC booking (simpler than Advanced JB). */
export interface PacProduct {
  box_type: string | null;
  product: string | null;
  weight: number;
  quantity: number;
  description: string;
}

export function createEmptyPacProduct(): PacProduct {
  return {
    box_type: null,
    product: null,
    weight: 0,
    quantity: 1,
    description: '',
  };
}

/** Same shape as a regular pickup product, but box_type is locked to a pallet. */
export function createEmptyPacPalletProduct(): PacProduct {
  return { ...createEmptyPacProduct(), box_type: 'Pallet' };
}

// TODO: replace with openapi.PostPacJobBookApi_Body once the backend adds the endpoint.
export interface PacJobBookApiBody {
  customer_code: string;
  caller: string;
  phone: string;
  REF1: string;
  REF2: string;
  REF3: string;
  stops: BookingStop[];
  products: PacProduct[];
}

export function buildPacBookApiBody(
  customer: JobBookingCustomer,
  stops: BookingStop[],
  products: PacProduct[],
): PacJobBookApiBody {
  return {
    customer_code: customer.customer_code ?? '',
    caller: customer.caller,
    phone: customer.phone,
    REF1: customer.REF1,
    REF2: customer.REF2,
    REF3: customer.REF3,
    stops,
    products,
  };
}

/** Package (box type) options — a fixed list, not an API catalog. */
export const PAC_PACKAGE_OPTIONS: Item[] = [
  'Pallet',
  '36ltr',
  '10kg',
  '5kg',
  'Mango box',
  'Green Mango box',
].map((value) => ({ value, title: value }));

const PAC_PRODUCE: Item[] = [
  { value: 'LONG MELON', title: 'Long Melon' },
  { value: 'WINTER MELON', title: 'Winter Melon' },
  { value: 'SNAKE BEAN', title: 'Snake Bean' },
  { value: 'OKRA', title: 'Okra' },
  { value: 'SNAKE GOURD', title: 'Snake Gourd' },
  { value: 'SINQUA', title: 'Sinqua' },
  { value: 'LUFFA', title: 'Luffa' },
  { value: 'HOG PLUM', title: 'Hog Plum' },
  { value: 'JUJUBE', title: 'Jujube' },
  { value: 'JACKFRUIT', title: 'Jackfruit' },
  { value: 'CHILLI', title: 'Chilli' },
  { value: 'PAW PAW', title: 'Paw Paw' },
  { value: 'BITTER MELON WHITE', title: 'Bitter Melon White' },
  { value: 'BITTER MELON BLACK', title: 'Bitter Melon Black' },
  { value: 'BUSH BITTER MELON', title: 'Bush Bitter Melon' },
  { value: 'CUCUMBER', title: 'Cucumber' },
  { value: 'RAMBUTAN', title: 'Rambutan' },
  { value: 'PEA EGGPLANT', title: 'Pea Eggplant' },
  { value: 'THAI EGGPLANT', title: 'Thai Eggplant' },
  { value: 'EGGPLANT', title: 'Eggplant' },
  { value: 'KANG KUNG', title: 'Kang Kung' },
  { value: 'CURRY LEAF', title: 'Curry Leaf' },
  { value: 'KAFIR LIME LEAF', title: 'Kafir Lime Leaf' },
  { value: 'PANDAN LEAF', title: 'Pandan Leaf' },
  { value: 'YAM BEAN', title: 'Yam Bean' },
  { value: 'BETEL LEAF', title: 'Betel Leaf' },
  { value: 'DRUMSTICK BEAN', title: 'Drumstick Bean' },
  { value: 'DRUMSTICK LEAF', title: 'Drumstick Leaf' },
  { value: 'GUAVA', title: 'Guava' },
  { value: 'PASSION FRUIT', title: 'Passion Fruit' },
  { value: 'BANANA', title: 'Banana' },
  { value: 'PUMPKIN', title: 'Pumpkin' },
  { value: 'WATER MELON', title: 'Water Melon' },
  { value: 'ZUCHINNI', title: 'Zuchinni' },
  { value: 'SAPHODILLA', title: 'Saphodilla' },
  { value: 'STAR APPLE', title: 'Star Apple' },
  { value: 'SPINACH', title: 'Spinach' },
  { value: 'LEMON', title: 'Lemon' },
  { value: 'LIMES', title: 'Limes' },
];

// Mango varieties keep explicit titles so abbreviations (KP, R2E2) are not mangled.
const PAC_MANGO_VARIETIES: Item[] = [
  { value: 'KP MANGO', title: 'KP Mango' },
  { value: 'R2E2 MANGO', title: 'R2E2 Mango' },
  { value: 'FALAN MANGO', title: 'Falan Mango' },
  { value: 'ELEPHANT MANGO', title: 'Elephant Mango' },
  { value: 'KEOW SAVOY', title: 'Keow Savoy' },
  { value: 'NAM DOC MAI', title: 'Nam Doc Mai' },
  { value: 'MAHA', title: 'Maha' },
  { value: 'CHOCODAM', title: 'Chocodam' },
  { value: 'CALYPSO', title: 'Calypso' },
  { value: 'COCONUT', title: 'Coconut' },
];

// Two groups (Produce / Mango varieties); disabled headers act as non-selectable group labels.
export const PAC_PRODUCT_OPTIONS: Item[] = [
  { value: '__group_produce', title: 'Produce', props: { disabled: true } },
  ...PAC_PRODUCE,
  {
    value: '__group_mango',
    title: 'Mango varieties',
    props: { disabled: true },
  },
  ...PAC_MANGO_VARIETIES,
];
